/* Card Holder ID edition is only present in dev and staging */

import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import { CardHolderIdResponse, getCardHolderId, updateCardHolderId } from 'lib/models/profile';
import { useEffect, useState } from 'react';
import useCardHolderIdEnabled from './useCardHolderIdEnabled';
import moment from 'moment';

type Props = {
	onClose: () => void;
	username?: string;
};

function CardHolderId({ onClose, username }: Props) {
	const [loadingState, setLoading] = useState(true);

	const { loading: loadingEnabled, enabled } = useCardHolderIdEnabled();

	const loading = loadingState || loadingEnabled;

	const [originalValue, setOriginalValue] = useState('');
	const [value, setValue] = useState('');
	const [previousCardHolderIds, setPreviousCardHolderIds] = useState<CardHolderIdResponse['previousCardHolderIds']>([]);

	const onSave = async () => {
		try {
			if (!username) {
				throw new Error("can't save without username");
			}
			await updateCardHolderId(username, value);
			onClose();
		} catch (error) {
			console.warn(error);
		}
	};

	useEffect(() => {
		const search = async () => {
			setLoading(true);
			if (!username) {
				setValue('');
				setOriginalValue('');
			} else {
				try {
					const response = await getCardHolderId(username);
					setValue(response.cardHolderId);
					setOriginalValue(response.cardHolderId);
					setPreviousCardHolderIds(response.previousCardHolderIds);
				} catch (error) {
					console.warn(error);
				}
			}
			setLoading(false);
		};
		search();
	}, [username]);

	return enabled && username ? (
		<Box
			sx={{
				padding: 2,
				gap: 2,
				backgroundColor: 'white',
				borderRadius: 5,
				borderColor: 'gray',
				borderWidth: 1,
				borderStyle: 'dotted',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
			>
				{loading && <CircularProgress />}
				<TextField
					value={value}
					label={'Card Holder ID'}
					onChange={(ev) => setValue(ev.target.value)}
					disabled={loading}
					fullWidth={true}
				/>
				<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
					<Button variant="contained" color="primary" onClick={onSave} disabled={!username || originalValue === value}>
						{!value && !!originalValue ? 'Limpiar' : 'Guardar'}
					</Button>
					<Button variant="contained" color="error" onClick={onClose}>
						Cerrar
					</Button>
				</Box>
			</Box>

			<Box sx={{ mt: 2, mb: 2, color: 'red' }}>
				ADVERTENCIA: ESTA FUNCIONALIDAD DEBE SER UTILIZADA COMO ULTIMO RECURSO, SE REGISTRARA EL MOVIMIENTO CON EL
				USUARIO EN SESION Y LA FECHA.
			</Box>

			{previousCardHolderIds.length > 0 && (
				<Box sx={{ mt: 2 }}>
					<Typography variant="h5">Modificaciones</Typography>
					{previousCardHolderIds.map((previousCardHolder, index) => {
						return (
							<Box
								key={`previous-${index}`}
								sx={{ borderStyle: 'solid', borderWidth: 1, borderColor: 'lightblue', padding: 1 }}
							>
								<Typography
									variant="body1"
									sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}
								>
									Usuario:
									<Typography variant="body2">{previousCardHolder.user}</Typography>
								</Typography>
								<Typography
									variant="body1"
									sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}
								>
									Card Holder Id:
									<Typography variant="body2">{previousCardHolder.previousCardHolderId}</Typography>
								</Typography>
								<Typography
									variant="body1"
									sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}
								>
									Fecha
									<Typography variant="body2">{moment(previousCardHolder.date).format('DD/MM/YY hh:mm:ss')}</Typography>
								</Typography>
							</Box>
						);
					})}
				</Box>
			)}
		</Box>
	) : null;
}

export default CardHolderId;
